import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";
import { motionCopy, motionMedia } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];

export const MotionSection = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <section
      data-testid="motion-section"
      className="bg-[var(--strollee-bg)] px-6 lg:px-10 py-20 lg:py-24"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="flex items-end justify-between gap-12"
      >
        <div className="max-w-[560px]">
          <p
            data-testid="motion-eyebrow"
            className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--strollee-accent)]"
          >
            {motionCopy.eyebrow}
          </p>
          <h2 className="mt-4 text-[36px] lg:text-[48px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--strollee-ink)]">
            {motionCopy.headlineLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>
        </div>
        <p className="max-w-[340px] pb-2 text-base leading-relaxed text-[var(--strollee-muted)]">
          {motionCopy.support}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 1.02 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1.1, ease: [0.25, 1, 0.5, 1] }}
        className="mt-8 lg:mt-10"
      >
        <div className="relative h-[65vh] lg:h-[72vh] overflow-hidden rounded-[var(--strollee-radius-hero)] bg-[var(--strollee-dark)]">
          <video
            ref={videoRef}
            data-testid="motion-video"
            src={motionMedia.videoUrl}
            poster={motionMedia.posterUrl}
            aria-label={motionMedia.alt}
            autoPlay
            muted
            loop
            playsInline
            onLoadedData={() => videoRef.current?.play().catch(() => {})}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <button
            type="button"
            onClick={toggle}
            aria-label={playing ? "Pause video" : "Play video"}
            data-testid="motion-playpause-btn"
            className="absolute bottom-6 right-6 flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur-md transition-all duration-200 hover:bg-white/20"
          >
            {playing ? (
              <Pause size={16} strokeWidth={2} />
            ) : (
              <Play size={16} strokeWidth={2} className="ml-[2px]" />
            )}
          </button>
        </div>
      </motion.div>
    </section>
  );
};
